import { API_BASE_URL } from "../../props";
import { useSelector } from "react-redux";
import { includes, map, pluck, reject } from "ramda";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  doGet,
  doPut,
  useGet,
  useGetWithNext,
  usePost,
  useUpload,
  useDelete,
  usePut,
  useUpdate,
} from "../../common/fetchServices";
import { getQueryParams } from "../../util/fetchUtils";
import { useCurrentOrgId } from "../../auth/authUtils";
import { useDeleteModuleItems } from "../modules/moduleServices";
import { notBlank } from "../../util/ext";

const getCreateAnthologyUrl = () => `${API_BASE_URL}/anthologies`;
const getAnthologyUrl = id => `${API_BASE_URL}/anthologies/${id}`;
const getPatchAnthologyUrl = ({ id }) => `${API_BASE_URL}/anthologies/${id}`;
const getAnthologyCollectionsUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/collections`;
const getAnthologyCollectionsDisplayOrderUpdateUrl = ({ id }) =>
  `${API_BASE_URL}/anthologies/${id}/collections/order`;
const getAnthologyMediaUrl = ({ id }) =>
  `${API_BASE_URL}/anthologies/${id}/media`;
const getAnthologyContentUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/content`;
const getAnthologyContentTypeSummaryUrl = id =>
  `${API_BASE_URL}/anthologies/${id}/content/content-type-summary`;
const getThumbnailUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/anthology/${id}/thumbnail`;
const getOrganizationLogoUploadUrl = ({ id }) =>
  `${API_BASE_URL}/admin/anthology/${id}/organization-logo`;
const getConvertToModuleUrl = ({ id }) =>
  `${API_BASE_URL}/admin/anthologies/${id}/convert-to-module`;

export const useAnthology = id => {
  const orgId = useCurrentOrgId();
  const url = getAnthologyUrl(id);
  return useGet(["anthology", orgId, { id }], url);
};

export const useAnthologyContent = ({ id, offset, limit }) => {
  const url =
    getAnthologyContentUrl(id) + "?" + getQueryParams({ offset, limit });
  return useGet(["anthologyMedia", { id }], url);
};

export const useAnthologyApprovedContent = (id, params) => {
  const orgId = useCurrentOrgId();
  const queryParams = getQueryParams({
    "ct.status": "APPROVED",
    ...params,
  });

  const url = getAnthologyContentUrl(id) + "?" + queryParams;

  return useGet(["anthologyApprovedMedia", orgId, { url }], url);
};

export const useAnthologyCreate = () => {
  return usePost(getCreateAnthologyUrl, {
    queriesToInvalidate: [
      ["search"],
      ["admin-media"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useAnthologyUpdate = () => {
  const queryClient = useQueryClient();
  const currentOrgId = useCurrentOrgId();

  return useUpdate(getPatchAnthologyUrl, {
    onSuccess: (data, variables) => {
      const anthology = variables.resourceIds;
      const queries = [
        ["admin-media"],
        ["search"],
        ["unorganized-content-groups"],
      ];
      for (const queryKey of queries) {
        queryClient.invalidateQueries({ queryKey });
      }
      queryClient.invalidateQueries({
        queryKey: ["anthology", currentOrgId, { id: anthology.id }],
      });

      if (notBlank(anthology.parentModules)) {
        anthology.parentModules.forEach(module => {
          queryClient.invalidateQueries({
            queryKey: ["moduleMedia", currentOrgId, { id: module.id }],
          });
          queryClient.invalidateQueries({
            queryKey: ["moduleDirectChildren", { id: module.id }],
          });
        });
      }
    },
  });
};

export const useUploadAnthologyThumbnail = () => {
  return useUpload(getThumbnailUploadUrl);
};

export const useUploadAnthologyOrganizationLogo = () => {
  return useUpload(getOrganizationLogoUploadUrl);
};

export const useAddToAnthology = () => {
  return usePost(getAnthologyMediaUrl, {
    queriesToInvalidate: [
      ["admin-media"],
      ["anthology", ["id"]],
      ["anthologyApprovedMedia"],
      ["anthologyCollections"],
      ["anthology-collections-count"],
      ["anthology-collections-creator"],
      ["collection"],
      ["content-status"],
      ["module-all-content"],
      ["module-all-content-count"],
      ["search"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useRemoveCollectionsFromAnthology = () => {
  const auth = useSelector(state => state.auth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ anthologyId, mediaIds }) => {
      const anthologyCollectionsUrl =
        getAnthologyCollectionsUrl(anthologyId) + "?limit=all";
      const media = await doGet(auth)(anthologyCollectionsUrl);
      const updated = reject(media => includes(media.id, mediaIds), media);
      const collectionMediaUrl = getAnthologyMediaUrl({ id: anthologyId });
      const body = { ids: pluck("id", updated) };

      return doPut(auth)(collectionMediaUrl, body);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["anthologyApprovedMedia"] });
      queryClient.invalidateQueries({
        queryKey: ["collection-approved-content"],
      });
      queryClient.invalidateQueries({ queryKey: ["anthologyCollections"] });
      queryClient.invalidateQueries({
        queryKey: ["anthology-collections-creator"],
      });
      queryClient.invalidateQueries({
        queryKey: ["unorganized-content-groups"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-media"] });
      queryClient.invalidateQueries({ queryKey: ["module-content-groups"] });
      variables.mediaIds.forEach(id =>
        queryClient.invalidateQueries({ queryKey: ["collection", { id }] }),
      );
    },
  });
};

export const useRemoveAllMediaFromAnthology = () => {
  return useDelete(getAnthologyMediaUrl, {
    queriesToInvalidate: [
      ["anthologyApprovedMedia"],
      ["anthologyCollections"],
      ["anthology-collections-creator"],
      ["collection-approved-content"],
      ["unorganized-content-groups"],
    ],
  });
};

export const useAnthologyDelete = () => {
  const queryClient = useQueryClient();
  const updateAnthology = useAnthologyUpdate();
  const removeAllMediaFromAnthology = useRemoveAllMediaFromAnthology();
  const deleteModuleItems = useDeleteModuleItems();

  return useMutation({
    mutationFn: async ({ anthology }) => {
      // Remove all parents and children before "deleting"
      await removeAllMediaFromAnthology.mutateAsync({
        resourceIds: { id: anthology.id },
      });

      if (notBlank(anthology.parentModules)) {
        await Promise.all(
          map(
            module =>
              deleteModuleItems.mutateAsync({
                id: module.id,
                ids: [anthology.id],
              }),
            anthology.parentModules,
          ),
        );
      }

      return updateAnthology.mutateAsync({
        resourceIds: anthology,
        data: {
          status: "DELETED",
          internalCode: null, // free up internal code value for use with other content groups
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["anthologyApprovedMedia"] });
      queryClient.invalidateQueries({
        queryKey: ["collection-approved-content"],
      });
      queryClient.invalidateQueries({ queryKey: ["anthologyCollections"] });
      queryClient.invalidateQueries({
        queryKey: ["anthology-collections-creator"],
      });
      queryClient.invalidateQueries({
        queryKey: ["unorganized-content-groups"],
      });
      queryClient.invalidateQueries({ queryKey: ["module-content-groups"] });
    },
  });
};

export const useAnthologyContentTypeSummary = (id, params) => {
  const orgId = useCurrentOrgId();

  const queryParams = getQueryParams({
    "ct.status": "APPROVED",
    ...params,
  });

  const url = getAnthologyContentTypeSummaryUrl(id) + "?" + queryParams;
  return useGet(["anthology-content-type-summary", orgId, { url }], url);
};

export const useUpdateAnthologyItems = () => {
  const currentOrgId = useCurrentOrgId();

  return usePut(getAnthologyMediaUrl, {
    queriesToInvalidate: [
      ["search"],
      ["anthology", currentOrgId, ["id"]],
      ["anthologyMedia", ["id"]],
      ["moduleMedia"],
      ["anthology-content-type-summary", ["id"]],
      ["anthology-collections-count", currentOrgId, ["id"]],
      ["anthologyCollections", currentOrgId, ["id"]],
      ["anthologyCollections"],
      ["anthology-collections-creator"],
      ["admin-media"],
    ],
  });
};

export const useAnthologyCollections = (id, params = {}, queryOptions = {}) => {
  const orgId = useCurrentOrgId();
  const queryParams = getQueryParams(params);
  const url = getAnthologyCollectionsUrl(id) + "?" + queryParams;
  return useGetWithNext(
    ["anthologyCollections", orgId, { url, id }],
    url,
    {},
    queryOptions,
  );
};

// update === [{"id": collectionId, "displayOrder": newDisplayOrder}, ...]
export const useUpdateAnthologyCollectionOrder = () => {
  const currentOrgId = useCurrentOrgId();
  return useUpdate(getAnthologyCollectionsDisplayOrderUpdateUrl, {
    queriesToInvalidate: [
      ["anthology-collections-creator", currentOrgId, ["id"]],
    ],
  });
};

export const useConvertToModule = () => {
  return usePost(getConvertToModuleUrl, {
    queriesToInvalidate: [
      ["admin-media"],
      ["anthology"],
      ["module-admin"],
      ["modules-creator"],
      ["unorganized-content-groups"],
      ["module-content-groups"],
    ],
  });
};
